<template>
    <entrance-page-layout>
        <form @submit.prevent="login(email, password, $event)" class="container" novalidate>
            <div class="login-row">
                <span class="user-img">
                    <img src="@/assets/gifs/user.gif"
                         alt="welcome back"
                         width="54"
                         height="54"
                    >
                </span>
                <span>
                    <router-link class="bodyStrong sign-up-link" to="/signup">Sign up</router-link>
                </span>
            </div>
            <span class="titleMedium">Welcome back</span>
            <SystemMessage :message="errorMessage"
                           v-if="errorMessage"
                           :dismiss-button="false"
                           class="error-message"></SystemMessage>
            <div class="inputs-container">
                <form-input v-model="email"
                            id="username"
                            placeholder="Email"
                            inputType="email"
                            :is-disabled="loading"
                            :maxLength="64"
                            icon="v2/user-circle"
                            icon-position="left"
                            :showValidationErrors="showValidationErrors"
                            :pattern="usernameRegex"
                            :patternError="EMAIL_FORMAT_ERROR"
                            @changedValue="inputValueChange($event, 'email')"
                            :isRequired="true"></form-input>
                <form-input v-model="password"
                            id="password"
                            placeholder="Password"
                            inputType="password"
                            class="password"
                            :is-disabled="loading"
                            :maxLength="24"
                            :isPassHidden="isPassHidden"
                            @showPasswordChanged="showPasswordChanged"
                            icon="v2/lock"
                            icon-position="left"
                            :showValidationErrors="showValidationErrors"
                            @changedValue="inputValueChange($event, 'password')"
                            :isRequired="true"></form-input>
            </div>
            <div class="login-row footer">
                <router-link class="body forgot-password-link" to="/forgot">
                    Forgot your password?
                </router-link>
                <Button id="login-button" type="submit" :loading="loading">Log in</Button>
            </div>
        </form>
    </entrance-page-layout>
</template>

<script>
import FormInput from 'components/inputs/formInput/FormInput.vue';
import SystemMessage from 'components/SystemMessage/SystemMessage.vue';
import links from 'services/metaData/links';
import { usernameRegex } from 'services/utils/regex';
import auth from 'services/auth';
import analytics from 'services/utils/analytics';
// eslint-disable-next-line import/no-cycle
import { navigateToAngularRoute } from 'services/utils/angularRouterUtils';
import { EMAIL_FORMAT_ERROR } from 'services/metaData/errors';
import Button from 'components/button/Button.vue';
import userDataService from 'services/userData';
import EntrancePageLayout from '../components/EntrancePageLayout.vue';

const wrongEmailOrPasswordErrorMessage = 'Wrong email or password';
const legacyConfirmEmailMessage = 'You must confirm your email address first!';
const emailConfirmationNeededErrorMessage = 'Please confirm your email';
const resetPasswordSuccess = 'Your password has been reset';

export default {
    name: 'LogInPage',
    components: {
        Button,
        SystemMessage,
        EntrancePageLayout,
        FormInput
    },
    data() {
        return {
            links,
            email: '',
            password: '',
            isPassHidden: true,
            showValidationErrors: false,
            emailValid: false,
            passwordValid: false,
            loading: false,
            usernameRegex,
            EMAIL_FORMAT_ERROR,
            errorMessage: ''
        };
    },
    beforeCreate() {
        if (auth.isLoggedIn()) {
            this.$router.push('/');
        }
    },
    mounted() {
        if (this.$route.query.reset) {
            this.$notification.open({ body: resetPasswordSuccess });
        }
    },
    methods: {
        showPasswordChanged(value) {
            this.isPassHidden = value;
        },
        inputValueChange({ isValid }, key) {
            if (key === 'email') {
                this.emailValid = isValid;
            } else if (key === 'password') {
                this.passwordValid = isValid;
            }
        },
        async login(email, password, event) {
            if (event.submitter) {
                event.submitter.focus();
            }

            if (!this.emailValid || !this.passwordValid) {
                this.showValidationErrors = true;
                return;
            }
            analytics.track('AccountLogin', 'loginClick');

            this.loading = true;

            try {
                await auth.login(email, password);
                await userDataService.fetchUserData();
                analytics.track('AccountLogin', 'loginSuccess');
                const to = this.$route.query.returnTo || '/';
                await navigateToAngularRoute(to.replace('/partners', ''));
            } catch (error) {
                analytics.track('AccountLogin', 'loginError');
                this.errorMessage = wrongEmailOrPasswordErrorMessage;

                if (error && error.response.data && error.response.data.error === legacyConfirmEmailMessage) {
                    this.errorMessage = emailConfirmationNeededErrorMessage;
                }
            }

            this.loading = false;
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide.V2";
@import "~styles/buttons.V2.scss";

.container {
    display: flex;
    position: relative;
    flex-direction: column;
    padding: $spacing-xxl-6;
}

.messageWrap.body.error-message.type-error {
    margin: $spacing-xl 0 0;
}

.login-row {
    display: flex;
    width: 100%;
    justify-content: space-between;
}

.footer {
    align-items: center;
}

.titleMedium {
    line-height: normal;
    color: $dark-500;
    margin-top: $spacing-xl;
}

.inputs-container {
    margin-top: $spacing-xl;
    margin-bottom: $spacing-xxl-6;
}

.sign-up-link {
    color: $primary-500;
    text-decoration: none;
}

.forgot-password-link {
    color: $light-900;
}

.password {
    margin-top: $spacing-m;
}

::v-deep #login-button {
    width: 144px;
}
</style>
